import { useState } from 'react';
import { Icon } from '../../components/ui/Icon';
import { RoleDataPreview } from './RoleDataPreview';
import type {
  User,
  Role,
  PermissionMatrix,
  Suite,
} from '../../data/types/platform';

export interface UserDrawerProps {
  user: User;
  roles: Role[];
  perms: PermissionMatrix;
  suites: Suite[];
  onClose: () => void;
}

export function UserDrawer({ user, roles, onClose }: UserDrawerProps) {
  // Preview-only role change. Persistence is intentionally not wired —
  // there is no write API yet (see Save button below).
  const [previewRoleId, setPreviewRoleId] = useState<string>(user.role);
  const isDirty = previewRoleId !== user.role;

  const currentRole = roles.find((r) => r.id === user.role);
  const previewRole = roles.find((r) => r.id === previewRoleId);
  const regions = user.regions ?? (user.region && user.region !== '—' ? [user.region] : ['all']);

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="user-drawer">
        <div className="drawer-head">
          <span className="user-avatar large">{user.initials}</span>
          <div className="drawer-head-text">
            <div className="drawer-name">{user.name}</div>
            <div className="drawer-meta">{user.email}</div>
            <div className="drawer-meta">{user.phone}</div>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="סגור" type="button">
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="drawer-body">
          <div className="drawer-block">
            <div className="drawer-block-head">פרטים</div>
            <div className="drawer-fields">
              <div>
                <span className="df-l">תפקיד</span>
                <span className="df-v">
                  <span className={'role-pill role-' + user.role}>
                    <span className="role-pill-short">{currentRole?.short}</span>
                    {currentRole?.he}
                  </span>
                </span>
              </div>
              <div><span className="df-l">צוות</span><span className="df-v">{user.team}</span></div>
              <div><span className="df-l">מחוז</span><span className="df-v">{user.region}</span></div>
              <div>
                <span className="df-l">סטטוס</span>
                <span className="df-v">
                  <span className={'status-pill status-' + user.status}>
                    <span className="status-dot" />
                    {user.status === 'active' ? 'פעיל' : user.status === 'pending' ? 'ממתין' : 'לא פעיל'}
                  </span>
                </span>
              </div>
              <div><span className="df-l">פעילות אחרונה</span><span className="df-v cell-mono">{user.last}</span></div>
              <div><span className="df-l">נוסף ב</span><span className="df-v cell-mono">{user.joined}</span></div>
              <div>
                <span className="df-l">אימות דו-שלבי</span>
                <span className="df-v">
                  {user.mfa ? (
                    <span className="mfa-on"><Icon name="shield-check" size={12} /> פעיל</span>
                  ) : (
                    <span className="mfa-off"><Icon name="shield-alert" size={12} /> כבוי</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="drawer-block">
            <div className="drawer-block-head">תצוגה מקדימה של גישה</div>
            <div className="rdp-role-edit">
              <label className="rdp-role-edit-label">
                <Icon name="user-cog" size={12} /> ערוך תפקיד (תצוגה מקדימה)
              </label>
              <select
                className="admin-select"
                value={previewRoleId}
                onChange={(e) => setPreviewRoleId(e.target.value)}
              >
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.he}</option>
                ))}
              </select>
              {isDirty && (
                <span className="rdp-dirty">
                  שינוי לא נשמר · מציג גישה ל-{previewRole?.he}
                </span>
              )}
            </div>
            <RoleDataPreview roleId={previewRoleId} regions={regions} dense />
          </div>
        </div>

        <div className="drawer-foot">
          <button className="admin-btn ghost" type="button"><Icon name="mail" size={14} /> שלח אימייל</button>
          <button
            className="admin-btn primary"
            type="button"
            disabled={!isDirty}
            title={isDirty ? 'שמירה תופעל לאחר חיווט API' : 'אין שינוי לשמירה'}
            onClick={(e) => e.preventDefault()}
          >
            <Icon name="save" size={14} /> שמור שינוי תפקיד
          </button>
          {user.status === 'active' && (
            <button className="admin-btn danger" type="button"><Icon name="user-x" size={14} /> השעה</button>
          )}
          {user.status === 'pending' && (
            <button className="admin-btn primary" type="button"><Icon name="send" size={14} /> שלח שוב</button>
          )}
        </div>
      </aside>
    </>
  );
}
